import { useMemo } from 'react';

import { UserInformationAddress } from '@rover/rsdk/src/apiClient/latest';
import useCurrentUserShim from '@rover/shared/js/hooks/useCurrentUserShim';
import useFlag, { FLAGS } from '@rover/shared/js/hooks/useFlag';

import { formatAddress } from '../utils';

type UseHomeAddress = {
  formattedAddress: string | null | void;
  address: UserInformationAddress;
  enabled: boolean;
};

/**
 * Hook to get the authenticated user's home address
 * Returns formatted address string, address object, and update method
 * Returns null values for formattedAddress and address if user is not authenticated
 * or if the user is not in the variant experience.
 */
const useHomeAddress = (): UseHomeAddress => {
  const { user } = useCurrentUserShim();
  const rolloutDefaultToHomeAddress = useFlag(FLAGS.ROLLOUT_DEFAULT_TO_HOME_ADDRESS);
  const address = user?.address || null;

  const formattedAddress = useMemo(() => {
    return formatAddress(address);
  }, [address]);

  const shouldUseHomeAddress = Boolean(rolloutDefaultToHomeAddress);

  if (!shouldUseHomeAddress) {
    return {
      formattedAddress: null,
      address: null,
      enabled: false,
    };
  }

  return {
    formattedAddress,
    address,
    enabled: true,
  };
};

export default useHomeAddress;
